<template>
  <metainfo>
    <template v-slot:title="{ content }">{{ content }}</template>
    <template v-slot:htmlAttrs="{ content }">{{ content }}</template>
    <template v-slot:bodyAttrs="{ content }">{{ content }}</template>
  </metainfo>
  <div class="container">
    <placeholder name="jss-header" :rendering="route" :page="{ ...route }" />
    <div ref="mainRef" class="jss-main-wrapper">
      <placeholder name="jss-main" :rendering="route" :page="{ ...route }" />
    </div>
    <placeholder name="jss-footer" :rendering="route" :page="{ ...route }" />
  </div>
  <content-loading class="layout-loading" :loading="showLoading" />
  <toast ref="toastRef" />
</template>

<script>
import {
  defineComponent,
  onMounted,
  onUnmounted,
  onUpdated,
  provide,
  reactive,
  ref,
  toRefs,
  watch,
} from 'vue';
import { useActiveMeta } from 'vue-meta';
import { appendMeta, canUseDOM, getScrollbarWidth } from '@/utils/dom-utils';
import 'video.js/dist/video-js.min.css';
import './styles/app.scss';
import { webStorage } from '@/utils/web-storage';
import { S_LANG } from '@/utils/web-storage-keys';
import { getSiteError } from '@/services/siteService';
import {
  getCurrentAlpha2Code,
  getGlobalConfigs,
  loadOneTrust,
  settingValue,
} from '@/utils/site-utils';
import { gtmPush } from '@/utils/gtm-utils';
import { enableHubSpotAutoLoad } from '@/utils/hubspot-utils';
import { captureLinkSource } from '@/utils/linksource-utils';
import { initLeadFormConfig } from '@/utils/form-utils';
import { getCanonicalUrl, truncateDesc, truncateTitle } from '@/utils/seo-utils';
import { isNoW } from '@/utils/obj-utils';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { concatUrls, isAbsoluteUri, redirectToLogin } from '@/utils/uri-utils';
import config from '@/temp/config';
import { eq } from '@/utils/string-utils';
import _ from 'lodash';
import useAppStore from '@/store/appStore';
import api from '@/api';
import useRevealAnimations from '@/hooks/useRevealAnimations';
import utils from './utils';

export default defineComponent({
  name: 'Layout',
  props: {
    route: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props, ctx) {
    let checkLoginTimeout;
    let hubSpotCleanup;
    const { t } = useI18n();
    const router = useRouter();
    const appStore = useAppStore();
    const [domain, cookieVersion, Robots] = getGlobalConfigs(
      props.route,
      'domain',
      'cookieVersion',
      'Robots'
    );
    initLeadFormConfig(props.route);
    const { noindex, nofollow } = props.route.fields || {};
    const { fullPath, params, query } = router.currentRoute.value ?? {};
    const { sc_mode } = query ?? {};
    const robotsMeta = [];
    noindex?.value && robotsMeta.push('noindex');
    nofollow?.value && robotsMeta.push('nofollow');

    const formatDateTime = (dateStr) => {
      if (isNoW(dateStr)) return '';
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      const hours = String(date.getUTCHours()).padStart(2, '0');
      const minutes = String(date.getUTCMinutes()).padStart(2, '0');
      const seconds = String(date.getUTCSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    };

    const DETAIL_PAGE_BODY_FIELD = {
      NewsDetail: 'newsBody',
      ArticlesDetail: 'articleBody',
      WebinarsDetail: 'webinarsBody',
      DownloadDetail: 'body',
    };
    const findDetailRendering = (renderings) => {
      for (const rendering of renderings || []) {
        if (DETAIL_PAGE_BODY_FIELD[rendering?.componentName]) return rendering;
        const nested = findDetailRendering(Object.values(rendering?.placeholders || {}).flat());
        if (nested) return nested;
      }
      return null;
    };
    const detailRendering = findDetailRendering(props.route?.placeholders?.['jss-main']);
    const detailBodyField = detailRendering
      ? DETAIL_PAGE_BODY_FIELD[detailRendering.componentName]
      : null;
    const detailDescription = detailRendering
      ? props?.route?.fields?.pageDescription?.value ||
        truncateDesc(
          detailRendering?.fields?.[detailBodyField]?.value ||
            props?.route?.fields?.[detailBodyField]?.value ||
            '',
          175
        )
      : '';

    const hrefLangList = [];
    const hrefLangValue = () => props?.route?.fields?.hrefLang?.value;
    if (!isNoW(hrefLangValue())) {
      const decodedValue = decodeURIComponent(
        decodeURIComponent(hrefLangValue().replace(/\+/g, ' '))
      );
      const pairs = decodedValue.split('&');
      pairs.forEach((pair) => {
        const [lang, url] = pair.split('=');
        if (lang && url) {
          hrefLangList.push({
            rel: 'alternate',
            hreflang: lang,
            href: url,
          });
        }
      });
    }
    if (detailRendering) {
      const rawPath = (fullPath || '').split('#')[0].split('?')[0] || '/';
      const langPrefix = params?.lang;
      let contentPath = rawPath;
      if (langPrefix) {
        const prefixStr = `/${langPrefix}`;
        if (rawPath === prefixStr || rawPath.startsWith(`${prefixStr}/`)) {
          contentPath = rawPath.slice(prefixStr.length) || '/';
        }
      }
      const fixedHrefLangs = [
        {
          rel: 'alternate',
          hreflang: 'en',
          href: `https://www.probiocdmo.com${contentPath}`,
        },
        {
          rel: 'alternate',
          hreflang: 'zh-cn',
          href: `https://www.probiocdmo.cn${contentPath}`,
        },
      ];
      for (let i = hrefLangList.length - 1; i >= 0; i--) {
        if (hrefLangList[i].hreflang === 'en' || hrefLangList[i].hreflang === 'zh-cn') {
          hrefLangList.splice(i, 1);
        }
      }
      hrefLangList.push(...fixedHrefLangs);
    }
    if (hrefLangList.length > 0) {
      hrefLangList.push({
        rel: 'alternate',
        hreflang: 'x-default',
        href: hrefLangList[0].href,
      });
    }
    const metas = {
      // 详情页标题超过 60 字符时，在最近的单词边界处截断并追加 "..."，保证单词完整
      // SSR 与客户端一致，避免水合后标题跳变
      title: detailRendering
        ? truncateTitle(props?.route?.fields?.pageTitle?.value)
        : props?.route?.fields?.pageTitle?.value,
      description: detailDescription || props?.route?.fields?.pageDescription?.value,
      htmlAttrs: {
        lang: props?.route?.fields?.language?.fields?.phrase?.value
          ? props?.route?.fields?.language?.fields?.phrase?.value
          : 'en',
        prefix: 'og: https://ogp.me/ns#',
        itemtype: 'https://schema.org/Product',
      },
      meta: [],
      link: [],
      script: [],
    };

    if (!isNoW(props?.route?.fields?.pageKeywords?.value)) {
      metas.meta.push({ name: 'keywords', content: props?.route?.fields?.pageKeywords?.value });
    }

    if (!isNoW(props.route.itemLanguage)) {
      metas.meta.push({
        property: 'og:locale',
        content: props.route.itemLanguage?.replace(/-/gi, '_'),
      });
    }

    metas.meta.push({
      property: 'og:type',
      content: props?.route?.fields?.ogType?.value || 'article',
    });

    metas.meta.push({
      property: 'og:title',
      content: props?.route?.fields?.ogTitle?.value || props?.route?.fields?.pageTitle?.value,
    });

    metas.meta.push({
      property: 'og:description',
      content: utils.string.ifEmpty(
        props?.route?.fields?.ogDescription?.value,
        props?.route?.fields?.pageDescription?.value
      ),
    });

    if (!isNoW(props?.route?.fields?.ogImage?.value)) {
      // eslint-disable-next-line vue/no-setup-props-destructure
      const ogImageSrc = props?.route?.fields?.ogImage?.value?.src;
      metas.meta.push({
        property: 'og:image',
        content: isAbsoluteUri(ogImageSrc)
          ? ogImageSrc
          : concatUrls(canUseDOM() ? window.location.origin : domain?.value, ogImageSrc),
      });
      if (!isNoW(props?.route?.fields?.ogImage?.value?.width)) {
        metas.meta.push({
          property: 'og:image:width',
          content: props?.route?.fields?.ogImage?.value?.width,
        });
      }
      if (!isNoW(props?.route?.fields?.ogImage?.value?.height)) {
        metas.meta.push({
          property: 'og:image:height',
          content: props?.route?.fields?.ogImage?.value?.height,
        });
      }
      if (!isNoW(props?.route?.fields?.ogImage?.value?.type)) {
        metas.meta.push({
          property: 'og:image:type',
          content: props?.route?.fields?.ogImage?.value?.type,
        });
      }
    }

    metas.meta.push({ property: 'og:url', content: getCanonicalUrl(fullPath) });

    if (!isNoW(props?.route?.fields?.ogUpdatedTime?.value)) {
      metas.meta.push({
        property: 'og:updated_time',
        content: formatDateTime(props?.route?.fields?.ogUpdatedTime?.value),
      });
    }

    if (!isNoW(props?.route?.fields?.articleSection?.value)) {
      metas.meta.push({
        property: 'article:section',
        content: props?.route?.fields?.articleSection?.value,
      });
    }

    if (!isNoW(props?.route?.fields?.articleTag?.value)) {
      metas.meta.push({
        property: 'article:tag',
        content: props?.route?.fields?.articleTag?.value,
      });
    }

    if (!isNoW(props?.route?.fields?.articlePublishedTime?.value)) {
      metas.meta.push({
        property: 'article:published_time',
        content: formatDateTime(props?.route?.fields?.articlePublishedTime?.value),
      });
    }

    if (!isNoW(props?.route?.fields?.articleModifiedTime?.value)) {
      metas.meta.push({
        property: 'article:modified_time',
        content: formatDateTime(props?.route?.fields?.articleModifiedTime?.value),
      });
    }

    const twitterDomain = domain?.value;
    if (!isNoW(twitterDomain)) {
      metas.meta.push({ name: 'twitter:domain', content: twitterDomain });
    }

    if (!isNoW(props?.route?.fields?.ogTitle?.value)) {
      metas.meta.push({
        name: 'twitter:title',
        content: props?.route?.fields?.ogTitle?.value,
      });
    }

    if (!isNoW(props?.route?.fields?.ogDescription?.value)) {
      metas.meta.push({
        name: 'twitter:description',
        content: props?.route?.fields?.ogDescription?.value,
      });
    }

    if (!isNoW(props?.route?.fields?.ogImage?.value?.src)) {
      metas.meta.push({
        name: 'twitter:image',
        content: props?.route?.fields?.ogImage?.value?.src,
      });
    }

    metas.meta.push({
      name: 'twitter:card',
      content: props?.route?.fields?.twitterCard?.value || 'summary_large_image',
    });

    if (!isNoW(props?.route?.fields?.ogTitle?.value)) {
      metas.meta.push({
        name: 'title',
        content: props?.route?.fields?.ogTitle?.value,
      });
    }

    if (!detailRendering && !isNoW(props?.route?.fields?.ogDescription?.value)) {
      metas.meta.push({
        name: 'description',
        content: props?.route?.fields?.ogDescription?.value,
      });
    }

    if (!isNoW(props?.route?.fields?.ogImage?.value)) {
      metas.meta.push({ property: 'image', content: props?.route?.fields?.ogImage?.value?.src });
    }

    if (!isNoW(props?.route?.fields?.author?.value)) {
      metas.meta.push({
        name: 'author',
        content: props?.route?.fields?.author?.value,
      });
    }

    if (!isNoW(props?.route?.fields?.date?.value)) {
      metas.meta.push({
        name: 'date',
        content: formatDateTime(props?.route?.fields?.date?.value),
      });
    }

    if (!isNoW(props?.route?.fields?.aiShortSummary?.value)) {
      metas.meta.push({
        name: 'ai-short-summary',
        content: props?.route?.fields?.aiShortSummary?.value,
      });
    }

    if (!isNoW(props?.route?.fields?.aiLongSummary?.value)) {
      metas.meta.push({
        name: 'ai-long-summary',
        content: props?.route?.fields?.aiLongSummary?.value,
      });
    }

    metas.link.push({
      rel: 'canonical',
      href: !isNoW(props?.route?.fields?.canonical?.value)
        ? props?.route?.fields?.canonical?.value
        : getCanonicalUrl(fullPath),
    });

    if (!isNoW(props.route.fields?.structuredData?.value)) {
      metas.script.push({
        innerHTML: props.route.fields?.structuredData?.value,
        type: 'application/ld+json',
      });
    }

    if (robotsMeta.length > 0) {
      metas.meta.unshift({ name: 'robots', content: robotsMeta.join(',') });
    }
    if (hrefLangList.length > 0) {
      metas.link.push(...hrefLangList);
    }
    const metaInfo = useActiveMeta();
    for (let metaName of Object.keys(metas)) {
      metaInfo[metaName] = metas[metaName];
    }
    const state = reactive({
      containerRef: null,
      toastRef: null,
      showLoading: false,
    });
    const mainRef = ref(null);
    useRevealAnimations(mainRef, () => props.route);
    // const onResize = () => {
    //   const docWidth = document.documentElement.clientWidth || document.body.clientWidth;
    //   document.documentElement.style.fontSize = `${Math.floor((docWidth * 100) / 48) / 100}px`;
    // };
    const pushPageData = () => {
      const { contentGroup, productName, productVariant } = props.route.fields || {};
      const data = {
        event: 'page_data',
        platform_name: 'website',
        content_group: settingValue(contentGroup),
        page_language: props.route.itemLanguage.toLowerCase(),
        page_country: getCurrentAlpha2Code(),
      };
      if (!isNoW(productName?.value)) {
        data.product_name = productName?.value;
      }
      if (!isNoW(productVariant?.value)) {
        data.product_variant = productVariant?.value;
      }
      gtmPush(data);
    };
    const toastProvider = {
      async show(options) {
        await state.toastRef.open(options);
      },
      async showEx(ex, opts = null) {
        const msg = await getSiteError(ex);
        await toastProvider.showError(msg, opts);
      },
      async showError(msg, opts = null) {
        if (!state.toastRef) return;
        await state.toastRef.open(
          _.merge(
            {
              message: msg,
              centered: true,
              closable: true,
              duration: 3200,
              showOk: true,
              okText: t('Go back').toUpperCase(),
            },
            opts
          )
        );
      },
      async showSuccess(msg, opts = null) {
        await toastProvider.show(
          _.merge(
            {
              message: msg,
              icon: 'ok-bucket',
              duration: 3200,
              closable: true,
            },
            opts
          )
        );
      },
    };
    const loadingProvider = {
      show() {
        state.showLoading = true;
        document.documentElement.classList.add('loading-open');
      },
      hide() {
        state.showLoading = false;
        document.documentElement.classList.remove('loading-open');
      },
    };
    const appMethods = {
      async logout() {
        loadingProvider.show();
        await api.cidp.signOut(null, {
          registerSource: '202',
        });
        appStore.setLoginInfo(null);
        loadingProvider.hide();
      },
      async logoutToLogin() {
        loadingProvider.show();
        await api.cidp.signOut(null, {
          registerSource: '202',
        });
        appStore.setLoginInfo(null);
        redirectToLogin(props.route);
      },
    };
    provide('toast', toastProvider);
    provide('loading', loadingProvider);
    provide('appMethods', appMethods);
    watch(
      () => props.route,
      () => {
        if (canUseDOM()) {
          document.documentElement.classList.remove(
            'modal-open',
            'menu-open',
            'loading-open',
            'market-detector-open'
          );
          setTimeout(() => {
            setPageTitle();
          }, 100);
        }
      },
      {
        deep: true,
      }
    );

    watch(
      () => props.route,
      (route) => {
        if (canUseDOM()) {
          webStorage.set(S_LANG, route.itemLanguage);
        }
      },
      {
        immediate: true,
        deep: true,
      }
    );
    if (canUseDOM()) {
      const viewerCountry = (localStorage.getItem('viewer-country') || '').toLowerCase();
      if (viewerCountry.includes('jp') && props.route?.fields?.redirectPage?.value?.href) {
        // eslint-disable-next-line vue/no-setup-props-destructure
        window.location.href = props.route.fields.redirectPage.value.href;
      }
      captureLinkSource();
      pushPageData();
      if (!eq(sc_mode, 'edit')) {
        loadOneTrust(props.route).catch();
      }
    }
    onMounted(() => {
      const lastUpdate = cookieVersion?.value;
      const savedUpdate = localStorage.getItem('cookieVersion');
      if (lastUpdate !== savedUpdate && savedUpdate) {
        if (window.Cookiebot && window.Cookiebot.renew) {
          window.Cookiebot.renew();
        }
      }
      localStorage.setItem('cookieVersion', lastUpdate);
      const scrollBarWidth = getScrollbarWidth();
      document.documentElement.style.setProperty('--scrollbar', `${scrollBarWidth}px`);
      // window.addEventListener('resize', onResize);
      // onResize();
      if (canUseDOM()) {
        hubSpotCleanup = enableHubSpotAutoLoad(props.route);
      }
    });
    onUnmounted(() => {
      if (checkLoginTimeout) {
        clearTimeout(checkLoginTimeout);
      }
      // window.removeEventListener('resize', onResize);
      if (hubSpotCleanup) {
        hubSpotCleanup();
        hubSpotCleanup = null;
      }
    });
    onUpdated(
      () => {
        pushPageData();
      },
      () => props.route
    );
    return {
      ...toRefs(state),
      mainRef,
    };
  },
});
</script>
